import { useCallback, useEffect, type RefObject } from 'react';
import { renderSinglePrintPageToPng } from '../utils/printScreenshot';

function waitForPrintRender(): Promise<void> {
  return new Promise(resolve => {
    // Two frames gives React a chance to commit prepared print state before the browser opens print preview.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function waitForPrintImages(root: HTMLElement | null): Promise<void> {
  if (!root) return;

  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(image => {
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();

      if (typeof image.decode === 'function') {
        // Print preview can capture blank boxes if data-URL images have not decoded yet.
        return image.decode().catch(() => undefined);
      }

      return new Promise<void>(resolve => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    })
  );
}

export type UsePreparedPrintOptions = {
  rootRef: RefObject<HTMLElement | null>;
  prepare?: () => Promise<void> | void;
  errorMessage?: string;
  enableScreenshot?: boolean;
  screenshotReady?: boolean;
};

export function usePreparedPrint({ rootRef, prepare, errorMessage = 'Unable to prepare this page for printing. Please try again.', enableScreenshot = false, screenshotReady = true }: UsePreparedPrintOptions) {
  const prepareRoot = useCallback(async () => {
    await prepare?.();
    await waitForPrintRender();

    const root = rootRef.current;
    if (!root) throw new Error('The print report is not mounted.');

    await waitForPrintImages(root);
    return root;
  }, [prepare, rootRef]);

  const print = useCallback(async () => {
    try {
      await prepareRoot();
      window.print();
    } catch (err) {
      console.error('Print Error:', err);
      window.alert(errorMessage);
    }
  }, [errorMessage, prepareRoot]);

  const screenshot = useCallback(() => {
    if (!screenshotReady) {
      window.alert('Run an analysis before copying the print image.');
      return;
    }

    try {
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        throw new Error('Image clipboard access is unavailable. Use localhost or HTTPS in a supported browser.');
      }

      const png = prepareRoot().then(renderSinglePrintPageToPng);
      void navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]).catch(err => {
        console.error('Screenshot Error:', err);
        window.alert('Unable to copy the print image. Check clipboard permission and try again.');
      });
    } catch (err) {
      console.error('Screenshot Error:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to copy the print image.');
    }
  }, [prepareRoot, screenshotReady]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.repeat) return;

      const key = event.key.toLowerCase();
      if (enableScreenshot && event.altKey && !event.ctrlKey && !event.metaKey && key === 's') {
        event.preventDefault();
        screenshot();
        return;
      }

      if (!(event.ctrlKey || event.metaKey) || key !== 'p') return;

      // Native Ctrl/Cmd+P would run before charts are snapshotted into the print portal.
      event.preventDefault();
      void print();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableScreenshot, print, screenshot]);

  return print;
}
